import { useGetAllDepartmentsQuery } from '@/app/features/employee/emp-dashboard/emp-dashboard.api';
import {
  useCreateAdminKPIPlanMutation,
  useGetAdminKPIPlanDetailsQuery,
  useUpdateAdminKPIPlanMutation,
} from '@/app/features/employee/performance/performance.api';
import type { CustomModalProps } from '@/components/CustomModal';
import useAuth from '@/hooks/useAuth';
import {
  Calendar03Icon,
  ChevronDownIcon,
  PencilEdit02Icon,
  XIcon,
} from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { DATE_FORMATS, formatDate } from '@/utils';
import {
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Flex,
  Form,
  Input,
  Select,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import CustomDrawer from '../../../../../components/CustomDrawer';

const { Text } = Typography;

type FormType = {
  plan_name: string;
  plan_type: string;
  plan_period: string;
  start_date: dayjs.Dayjs;
  end_date: dayjs.Dayjs;
  bulk_due_date?: dayjs.Dayjs;
  line_manager_number: number[];
  departments: {
    department_id: string;
    name: string;
    due_date: dayjs.Dayjs;
    customized: boolean;
  }[];
};

type Props = {
  open: boolean;
  id?: string;
  type?: 'VIEW' | 'EDIT' | 'ADD' | 'REOPEN';
} & CustomModalProps;

const ManageKpiSetUp = ({ open, handleClose, id, type }: Props) => {
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();

  const isView = type === 'VIEW';
  const isEdit = type === 'EDIT';
  const isReopen = type === 'REOPEN';

  const { data: deptData } = useGetAllDepartmentsQuery({});
  const { data: detailsData } = useGetAdminKPIPlanDetailsQuery(id as string, {
    skip: !id || type === 'ADD',
  });

  const departments = Form.useWatch('departments', form);
  const lineManagerNumber = Form.useWatch('line_manager_number', form);
  const planPeriod = Form.useWatch('plan_period', form);

  useEffect(() => {
    if (open && detailsData?.data) {
      const data = detailsData.data;
      form.setFieldsValue({
        plan_name: data.planName,
        plan_type: data.planType,
        plan_period: data.planPeriod,
        start_date: data.startDate ? dayjs(data.startDate) : undefined,
        end_date: data.endDate ? dayjs(data.endDate) : undefined,
        bulk_due_date: dayjs(),
        line_manager_number: Array.from(
          { length: data.lineManagerNumber || 0 },
          (_, i) => i + 1,
        ),
        departments: (data.departmentMappings || []).map((dm: any) => {
          const dept = (deptData?.data?.data || []).find(
            (d: any) => d.id === dm.departmentId,
          );
          return {
            department_id: dm.departmentId,
            name: dept?.name || 'n/a',
            due_date: dm.kpiDueDate ? dayjs(dm.kpiDueDate) : undefined,
            customized: dm.isCustomized || false,
          };
        }),
      } as any);
    } else if (
      open &&
      type === 'ADD' &&
      deptData?.data &&
      !form.getFieldValue('departments')
    ) {
      form.setFieldsValue({
        bulk_due_date: dayjs(),
        departments: (deptData?.data?.data || []).map((d: any) => ({
          department_id: d.id,
          name: d.name,
          due_date: dayjs(),
        })),
      } as any);
    }
  }, [open, detailsData, deptData, form, type]);

  const [createAdminKPIPlan, { isLoading: isCreating }] =
    useCreateAdminKPIPlanMutation();
  const [updateAdminKPIPlan, { isLoading: isUpdating }] =
    useUpdateAdminKPIPlanMutation();

  const onFinish = async ({
    line_manager_number,
    bulk_due_date,
    ...values
  }: FormType) => {
    if (isView) return;
    try {
      const payload = {
        ...values,
        admin_id: userId || '',
        organization_id: organizationId || '',
        start_date: formatDate(values.start_date, DATE_FORMATS.ISO_DATE),
        end_date: formatDate(values.end_date, DATE_FORMATS.ISO_DATE),
        departments: values.departments?.map((d) => ({
          department_id: d.department_id,
          due_date: formatDate(d.due_date, DATE_FORMATS.ISO_DATE),
        })),
      };

      if (isEdit && id) {
        const res = await updateAdminKPIPlan({
          id,
          body: { ...payload, plan_id: detailsData?.data?.id },
        }).unwrap();
        if (res?.success === false) {
          toast('error', getErrorMessage(res, 'Failed to update KPI Plan'));
          return;
        }
        toast('success', 'KPI Plan updated successfully');
      } else {
        // ADD and REOPEN both create a new plan
        const res = await createAdminKPIPlan({
          ...payload,
          line_manager_number: Math.max(...(line_manager_number || [])),
        }).unwrap();
        if (res?.success === false) {
          toast('error', getErrorMessage(res, 'Failed to create KPI Plan'));
          return;
        }
        toast('success', isReopen ? 'KPI Plan re-opened successfully' : 'KPI Plan created successfully');
      }
      onClose();
    } catch (error: any) {
      const message = getErrorMessage(error);
      toast(
        'error',
        message || `Failed to ${isEdit ? 'update' : isReopen ? 're-open' : 'create'} KPI Plan`,
      );
    }
  };

  const onClose = () => {
    handleClose();
    form.resetFields();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={onClose}
      title={
        isView
          ? 'View KPI Set-Up'
          : isEdit
            ? 'Edit KPI Set-Up'
            : isReopen
              ? 'Re-Open KPI Set-Up'
              : 'Open KPI Set-Up'
      }
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={onFinish}
        className="custom-form space-y-6"
        initialValues={{
          plan_type: 'Performance',
        }}
        onValuesChange={(changedValues, allValues) => {
          if (changedValues.plan_period || changedValues.start_date) {
            const period = allValues.plan_period;
            const start = allValues.start_date || dayjs().startOf('day');

            if (period) {
              let end = undefined;
              if (period === 'YEARLY') {
                end = start.add(1, 'year').subtract(1, 'day');
              } else if (period === 'HALF_YEARLY') {
                end = start.add(6, 'month').subtract(1, 'day');
              } else if (period === 'QUARTERLY') {
                end = start.add(3, 'month').subtract(1, 'day');
              }
              form.setFieldsValue({
                start_date: start,
                end_date: end,
              });
            }
          }
          if (changedValues.bulk_due_date) {
            const date = changedValues.bulk_due_date;
            const currentDepts = allValues.departments || [];
            form.setFieldsValue({
              departments: currentDepts.map((d: any) => ({
                ...d,
                due_date: date,
                customized: false,
              })),
            });
          }
        }}
      >
        <Form.Item
          label="Name"
          name="plan_name"
          className="mb-6"
          rules={[{ required: true, message: 'Please input name!' }]}
        >
          <Input
            disabled={isView}
            placeholder="Enter name"
            className="bg-theme-slate-900 border-theme-gray-800 text-white"
          />
        </Form.Item>

        <Flex gap={16} className="mb-6">
          <Form.Item
            label="Plan Type"
            name="plan_type"
            className="flex-1"
            rules={[{ required: true, message: 'Please select plan type!' }]}
          >
            <Select
              disabled
              placeholder="Select"
              suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
              className="bg-theme-slate-900"
              options={[{ label: 'Performance', value: 'Performance' }]}
            />
          </Form.Item>
          <Form.Item
            label="Plan Period"
            name="plan_period"
            className="flex-1"
            rules={[{ required: true, message: 'Please select plan period!' }]}
          >
            <Select
              disabled={isView}
              placeholder="Select"
              suffixIcon={<ChevronDownIcon size={20} className="text-white" />}
              className="bg-theme-slate-900"
              options={[
                { label: 'Yearly', value: 'YEARLY' },
                { label: 'Half Yearly', value: 'HALF_YEARLY' },
                { label: 'Quarterly', value: 'QUARTERLY' },
              ]}
            />
          </Form.Item>
        </Flex>

        <Flex gap={16} className="mb-6">
          <Form.Item
            label="Cycle Start Date"
            name="start_date"
            className="flex-1"
            rules={[{ required: true, message: 'Please select start date!' }]}
          >
            <DatePicker
              disabled={isView || !planPeriod}
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<Calendar03Icon size={20} className="text-white" />}
              className="bg-theme-slate-900 border-theme-gray-800"
              // disabledDate={(current) =>
              //   current && current < dayjs().startOf('day')
              // }
            />
          </Form.Item>
          <Form.Item
            label="Cycle End Date"
            name="end_date"
            className="flex-1"
            rules={[{ required: true, message: 'Please select end date!' }]}
          >
            <DatePicker
              disabled={true}
              format="DD/MM/YYYY"
              placeholder="dd/mm/yyyy"
              suffixIcon={<Calendar03Icon size={20} className="text-white" />}
              className="bg-theme-slate-900 border-theme-gray-800"
              disabledDate={(current) =>
                current && current < dayjs().startOf('day')
              }
            />
          </Form.Item>
        </Flex>

        <div className="mt-8">
          <Flex
            justify="space-between"
            align="center"
            className="mb-4 border-b border-theme-gray-800 pb-3"
          >
            <Text className="text-white font-semibold text-base">
              Department
            </Text>
            {isView || isEdit ? (
              <div className="flex gap-2 pr-1 opacity-70">
                <Flex gap={5}>
                  <Calendar03Icon size={18} className="text-theme-primary" />
                  <Text className="text-white font-medium">
                    {dayjs().format('DD/MM/YYYY')}
                  </Text>
                </Flex>
              </div>
            ) : (
              <Form.Item name="bulk_due_date" noStyle>
                <DatePicker
                  picker="date"
                  inputReadOnly
                  allowClear={false}
                  format="DD/MM/YYYY"
                  className="p-0 border-none !bg-transparent text-white font-medium cursor-pointer text-right w-[140px]"
                  disabledDate={(current) =>
                    current && current < dayjs().startOf('day')
                  }
                  suffixIcon={
                    <PencilEdit02Icon size={18} className="text-theme-primary cursor-pointer" />
                  }
                />
              </Form.Item>
            )}
          </Flex>

          {isView || isEdit ? (
            <Form.List name="departments">
              {(fields) => (
                <div className="max-h-[300px] overflow-y-auto custom-scrollbar grid grid-cols-4 gap-4 mt-6">
                  {fields.map(({ key, name }) => {
                    const dept = departments?.[name];
                    return (
                      <div
                        key={key}
                        className="p-3 bg-theme-slate-800 rounded-lg"
                      >
                        <Flex justify="space-between" align="start">
                          <div>
                            <span className="block text-xs font-normal text-theme-gray-300">
                              Department:
                            </span>
                            <span className="block text-sm font-medium text-white truncate w-full max-w-[140px]">
                              {dept?.name}
                            </span>
                          </div>
                          {!isView && !isEdit && (
                            <button className="cursor-pointer" type="button">
                              <XIcon size={16} className="text-white" />
                            </button>
                          )}
                        </Flex>
                        <Divider className="bg-theme-gray-800 py-0 my-2" />
                        <Flex align="center" gap={6} justify="start">
                          <Calendar03Icon
                            size={18}
                            className="text-theme-primary"
                          />
                          <Text className="text-theme-gray-300 font-medium">
                            {dept?.due_date
                              ? dayjs(dept.due_date).format('DD/MM/YYYY')
                              : 'n/a'}
                          </Text>
                        </Flex>
                      </div>
                    );
                  })}
                </div>
              )}
            </Form.List>
          ) : (
            <Form.List name="departments">
              {(fields) => (
                <div className="space-y-5 max-h-[300px] overflow-y-auto custom-scrollbar">
                  {fields.map(({ key, name }) => {
                    const dept = departments?.[name];

                    return (
                      <Flex key={key} justify="space-between" align="center">
                        <Form.Item
                          name={[name, 'customized']}
                          initialValue={false}
                          hidden
                        />
                        <Text className="text-theme-gray-300 text-[15px]">
                          {dept?.name}
                        </Text>
                        <div className="w-[220px] shrink-0 pr-3">
                          <Form.Item
                            noStyle
                            className="w-auto"
                            name={[name, 'due_date']}
                          >
                            <DatePicker
                              picker="date"
                              inputReadOnly
                              allowClear={false}
                              format="DD/MM/YYYY"
                              disabled={dept?.customized || false}
                              className="p-0 border-none !bg-transparent"
                              disabledDate={(current) =>
                                current && current < dayjs().startOf('day')
                              }
                              onChange={() => {
                                form.setFieldValue(
                                  ['departments', name, 'customized'],
                                  true,
                                );
                              }}
                              suffixIcon={
                                dept?.customized ? (
                                  <div className="status status-success">
                                    Customized
                                  </div>
                                ) : (
                                  <PencilEdit02Icon
                                    size={18}
                                    className="text-theme-primary cursor-pointer"
                                  />
                                )
                              }
                              prefix={
                                <Calendar03Icon
                                  size={18}
                                  className="text-theme-primary"
                                />
                              }
                            />
                          </Form.Item>
                        </div>
                      </Flex>
                    );
                  })}
                </div>
              )}
            </Form.List>
          )}
        </div>

        <Form.Item
          label="Line Manager Approval Flow"
          name="line_manager_number"
          className="mt-8 mb-0"
          rules={[{ required: true, message: 'Please select approval flow!' }]}
        >
          <Checkbox.Group
            value={lineManagerNumber}
            disabled={isView || isEdit}
            onChange={(checkedValues: number[]) => {
              if (isView || isEdit) return;

              const previous = lineManagerNumber || [];
              const added = checkedValues.find(
                (v: number) => !previous.includes(v),
              );
              const removed = previous.find(
                (v: number) => !checkedValues.includes(v),
              );

              let target = 0;
              if (added) {
                target = Number(added);
              } else if (removed) {
                target = Number(removed);
              } else {
                target = checkedValues.length
                  ? Math.max(...checkedValues.map(Number))
                  : 0;
              }

              const nextValues = Array.from(
                { length: target },
                (_, i) => i + 1,
              );
              form.setFieldValue('line_manager_number', nextValues);
            }}
          >
            <Space size={24}>
              {[1, 2, 3, 4, 5].map((num) => (
                <Checkbox
                  key={num}
                  value={num}
                  className="text-white custom-checkbox disabled-bg-transparent"
                  rootClassName="disabled:!bg-transparent"
                >
                  L-{num}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>

        {!isView && (
          <div className="flex justify-end mt-12 mb-6">
            <Button
              type="primary"
              htmlType="submit"
              className="btn-primary min-w-[100px]"
              loading={isCreating || isUpdating}
            >
              {isEdit ? 'Update' : isReopen ? 'Re-Open' : 'Set- Up'}
            </Button>
          </div>
        )}
      </Form>
    </CustomDrawer>
  );
};

export default ManageKpiSetUp;
